import React, { useEffect, useState } from "react";
import { Outlet, useParams, useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import IdleLogout from "./IdleLogout";
import api from "../lib/api";
import { initials } from "../lib/helpers";
import { hexToHslString, onColor } from "../lib/contrast";
import { getCache, setCache } from "../lib/cache";
import { applyAppIcon, firmIcon } from "../lib/appIcon";  // used by app-level icon manager (kept for reference)
import { useTheme } from "../context/ThemeContext";
import {
  LayoutGrid, Map, Package, Network, FileText, Activity as ActIcon,
  Bot, BookOpen, ChevronLeft, LogOut, PanelLeftClose, PanelLeft, Crosshair, Sun, Moon,
  Building2, GitBranch, FolderOpen, ClipboardCheck, ShieldAlert, IdCard, PhoneCall, Phone, Grid3x3,
  MessagesSquare, CalendarClock, UserRound, Briefcase,
  Users2, CreditCard, Tags, DollarSign, FileSignature, ClipboardList, Globe, Bell, Palette, LifeBuoy, Sparkles, Mail, Coins, Receipt, Landmark,
} from "lucide-react";
import { Button } from "./ui/button";
import { AppNotificationsBell } from "./AppNotificationsBell";
import SupportChat from "./SupportChat";
import { ContactSuggestionBanner } from "./NativeContacts";

const matterNav = [
  { to: "", label: "Case Map", icon: Map, end: true },
  { to: "appointments", label: "Upcoming Appointments", icon: CalendarClock },
  { to: "package", label: "Package & Services", icon: Package },
  { to: "claims", label: "Claims Map", icon: Network },
  { to: "research", label: "Legal Research", icon: BookOpen },
  { to: "documents", label: "Documents", icon: FileText },
  { to: "emails", label: "Email Communications", icon: Mail },
  { to: "summarizer", label: "Summarizer", icon: Sparkles },
  { to: "process", label: "Client Progress", icon: GitBranch },
  { to: "evidence", label: "Evidence Review", icon: FolderOpen },
  { to: "activity", label: "Activity", icon: ActIcon },
  { to: "assistant", label: "AI Assistant", icon: Bot },
];

const firmNav = [
  { key: "lawyers", label: "Lawyers", icon: Users2 },
  { key: "clients", label: "Clients", icon: Building2 },
  { key: "billing", label: "Billing", icon: CreditCard },
  { key: "aiusage", label: "AI Usage", icon: Coins, admin: true },
  { key: "services", label: "Services", icon: Tags, admin: true },
  { key: "pricing", label: "Bundled Services", icon: DollarSign, admin: true },
  { key: "payments", label: "Accept Payment", icon: Landmark, admin: true },
  { key: "firm-payments", label: "Firm Payments", icon: Receipt, admin: true },
  { key: "legal-armor", label: "Legal Armor", icon: Landmark, noStaff: true },
  { key: "agreements", label: "Agreements", icon: FileSignature, admin: true },
  { key: "forms", label: "Forms", icon: ClipboardList, admin: true },
  { key: "website", label: "Website & Appointments", icon: Globe, admin: true },
  { key: "waiting", label: "Waiting room", icon: CalendarClock, admin: true },
  { key: "notifications", label: "Notifications", icon: Bell, admin: true },
  { key: "branding", label: "Branding", icon: Palette, admin: true },
  { key: "ai", label: "AI Assistant", icon: Bot, admin: true },
  { key: "security", label: "Security Access", icon: ShieldAlert, admin: true },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const { matterId } = useParams();
  const nav = useNavigate();
  const loc = useLocation();
  const onFirm = loc.pathname === "/firm" || loc.pathname.startsWith("/firm/");
  const firmTab = new URLSearchParams(loc.search).get("tab") || "lawyers";
  let activeFirmTab = firmTab;
  if (loc.pathname.startsWith("/firm/agreements")) activeFirmTab = "agreements";
  else if (loc.pathname.startsWith("/firm/forms")) activeFirmTab = "forms";
  const [collapsedPref, setCollapsed] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const on = () => setIsMobile(mq.matches);
    on(); mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  const collapsed = collapsedPref && !isMobile;
  const [matter, setMatter] = useState(null);
  const [showSupport, setShowSupport] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => { setMobileOpen(false); }, [loc.pathname, loc.search]);

  useEffect(() => {
    if (!matterId) { setMatter(null); return; }
    const c = getCache("m:" + matterId);
    if (c) setMatter(c);
    api.get(`/matters/${matterId}`).then((r) => { setMatter(r.data); setCache("m:" + matterId, r.data); }).catch(() => {});
  }, [matterId]);

  const base = matterId ? `/matters/${matterId}` : "";
  const isAdmin = user?.tenant_role === "admin";
  const firmName = user?.display_name || user?.tenant_name || "Your firm";
  const brandAccent = user?.accent_color || "#3a5a40";
  const accentHsl = hexToHslString(brandAccent);
  const brandStyle = accentHsl ? { "--primary": accentHsl, "--ring": accentHsl, "--primary-foreground": hexToHslString(onColor(brandAccent)) } : undefined;
  const navCls = (active) => `w-full flex items-center gap-3 px-4 py-2.5 text-sm lift ${active ? "text-primary bg-primary/5" : "text-muted-foreground hover:text-foreground"}`;
  const isActive = (to, end) => {
    const full = to ? `${base}/${to}` : base;
    return end ? loc.pathname === full : loc.pathname.startsWith(full);
  };

  return (
    <div className="h-screen flex app-bg overflow-hidden" style={brandStyle}>
      {mobileOpen && <div className="fixed inset-0 bg-black/60 z-40 md:hidden" onClick={() => setMobileOpen(false)} data-testid="firm-mobile-backdrop" />}
      {/* Sidebar */}
      <aside
        className={`${collapsed ? "md:w-[68px]" : "md:w-[268px]"} w-[268px] shrink-0 surface hairline border-t-0 border-b-0 border-l-0 flex flex-col duration-200 fixed md:static inset-y-0 left-0 z-50 transition-transform md:translate-x-0 ${mobileOpen ? "translate-x-0" : "-translate-x-full"}`}
        data-testid="app-sidebar"
      >
        <div className={`h-16 flex items-center gap-3 ${collapsed ? "px-3 justify-center" : "px-4"} border-b border-white/8`} data-testid="firm-brand-header">
          {!collapsed && (user?.logo_url
            ? <img src={user.logo_url} alt={firmName} className="w-9 h-9 rounded-xl object-contain shrink-0 bg-white/5" />
            : <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${brandAccent}22`, border: `1px solid ${brandAccent}55` }}><Building2 size={18} style={{ color: brandAccent }} /></div>)}
          {!collapsed && (
            <div className="leading-tight overflow-hidden flex-1 min-w-0">
              <div className="font-display font-bold text-[15px] tracking-tight text-foreground truncate" data-testid="firm-brand-name">{firmName}</div>
              <div className="text-[10px] tracking-wide text-muted-foreground">Powered by Lexarra</div>
            </div>
          )}
          <button onClick={() => setCollapsed(!collapsedPref)} data-testid="toggle-sidebar" title={collapsed ? "Expand menu" : "Collapse menu"}
            className="hidden md:flex items-center justify-center w-8 h-8 rounded-lg surface-2 hairline text-muted-foreground hover:text-foreground lift shrink-0">
            {collapsed ? <PanelLeft size={16} /> : <PanelLeftClose size={16} />}
          </button>
          <button onClick={() => setMobileOpen(false)} data-testid="firm-mobile-close"
            className="md:hidden flex items-center justify-center w-8 h-8 rounded-lg surface-2 hairline text-muted-foreground hover:text-foreground lift shrink-0">
            <PanelLeftClose size={16} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto py-3">
          <button onClick={() => nav("/intake")} data-testid="nav-intake" className={navCls(loc.pathname === "/intake")}>
            <ClipboardCheck size={17} className="shrink-0" />{!collapsed && <span>Intake &amp; Consultations</span>}
          </button>

          <button onClick={() => nav("/conflicts")} data-testid="nav-conflicts" className={navCls(loc.pathname === "/conflicts")}>
            <ShieldAlert size={17} className="shrink-0" />{!collapsed && <span>Conflicts</span>}
          </button>

          {isAdmin && (
            <button onClick={() => nav("/identities")} data-testid="nav-identities" className={navCls(loc.pathname.startsWith("/identities"))}>
              <IdCard size={17} className="shrink-0" />{!collapsed && <span>Identities</span>}
            </button>
          )}

          {isAdmin && (
            <button onClick={() => nav("/payments")} data-testid="nav-payments" className={navCls(loc.pathname === "/payments")}>
              <Receipt size={17} className="shrink-0" />{!collapsed && <span>Payments</span>}
            </button>
          )}

          <button onClick={() => nav("/")} data-testid="nav-all-matters" className={navCls(loc.pathname === "/")}>
            <LayoutGrid size={17} className="shrink-0" />{!collapsed && <span>My Matters</span>}
          </button>

          {matterId && matter && (
            <div className="mb-2">
              {!collapsed && (
                <div className="px-4 pt-2 pb-1">
                  <div className="font-editorial text-[15px] leading-tight text-foreground line-clamp-2">{matter.name}</div>
                  <div className="text-[10px] text-muted-foreground mt-0.5 font-mono-legal">{matter.current_stage}</div>
                </div>
              )}
              {matterNav.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.to, item.end);
                return (
                  <button key={item.label} data-testid={`matter-nav-${item.to || "home"}`}
                    onClick={() => nav(item.to ? `${base}/${item.to}` : base)}
                    className={`w-full flex items-center gap-3 ${collapsed ? "px-4" : "pl-9 pr-4"} py-2 text-[13px] border-l-2 lift ${active ? "border-primary text-foreground bg-primary/5" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
                    <Icon size={16} className="shrink-0" />{!collapsed && <span>{item.label}</span>}
                  </button>
                );
              })}
            </div>
          )}

          {isAdmin && (
            <button onClick={() => nav("/firm-matters")} data-testid="nav-firm-matters" className={navCls(loc.pathname === "/firm-matters")}>
              <Briefcase size={17} className="shrink-0" />{!collapsed && <span>Firm Matters</span>}
            </button>
          )}

          <button onClick={() => nav("/firm")} data-testid="nav-firm" className={navCls(onFirm)}>
            <Building2 size={17} className="shrink-0" />
            {!collapsed && <span>Tenant Admin</span>}
          </button>
          {onFirm && !collapsed && (
            <div className="mb-1">
              {firmNav.filter((n) => (!n.admin || isAdmin) && (!n.noStaff || user?.tenant_role !== "staff")).map((item) => {
                const Icon = item.icon;
                const active = activeFirmTab === item.key;
                return (
                  <button key={item.key} data-testid={`firm-nav-${item.key}`}
                    onClick={() => nav(item.key === "lawyers" ? "/firm" : `/firm?tab=${item.key}`)}
                    className={`w-full flex items-center gap-3 pl-9 pr-4 py-2 text-[13px] border-l-2 lift ${active ? "border-primary text-foreground bg-primary/5" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
                    <Icon size={16} className="shrink-0" /><span>{item.label}</span>
                  </button>
                );
              })}
            </div>
          )}

          <button onClick={() => nav("/messages")} data-testid="nav-messages" className={navCls(loc.pathname === "/messages")}>
            <MessagesSquare size={17} className="shrink-0" />{!collapsed && <span>Messages</span>}
          </button>
          <button onClick={() => nav("/scheduling")} data-testid="nav-scheduling" className={navCls(loc.pathname === "/scheduling")}>
            <CalendarClock size={17} className="shrink-0" />{!collapsed && <span>Scheduling</span>}
          </button>
          <button onClick={() => nav("/emails")} data-testid="nav-emails" className={navCls(loc.pathname === "/emails")}>
            <Mail size={17} className="shrink-0" />{!collapsed && <span>Emails</span>}
          </button>
          {isAdmin && (
            <button onClick={() => nav("/ai-training")} data-testid="nav-ai-training" className={navCls(loc.pathname === "/ai-training")}>
              <Bot size={17} className="shrink-0" />{!collapsed && <span>AI Training</span>}
            </button>
          )}
          {isAdmin && (
            <button onClick={() => nav("/phone-api")} data-testid="nav-phone-api" className={navCls(loc.pathname === "/phone-api")}>
              <PhoneCall size={17} className="shrink-0" />{!collapsed && <span>Phone API</span>}
            </button>
          )}
          <button onClick={() => nav("/phone-calls")} data-testid="nav-phone-calls" className={navCls(loc.pathname === "/phone-calls")}>
            <Phone size={17} className="shrink-0" />{!collapsed && <span>Phone Calls</span>}
          </button>
          {user?.slug === "demo-firm" && (
            <button onClick={() => nav("/phone-dialer")} data-testid="nav-phone-dialer" className={navCls(loc.pathname === "/phone-dialer")}>
              <Grid3x3 size={17} className="shrink-0" />{!collapsed && <span>Phone Dialer</span>}
            </button>
          )}
          <button onClick={() => nav("/ai-research")} data-testid="nav-ai-research" className={navCls(loc.pathname === "/ai-research")}>
            <Sparkles size={17} className="shrink-0" />{!collapsed && <span>AI Research</span>}
          </button>
          <button onClick={() => nav("/document-types")} data-testid="nav-document-types" className={navCls(loc.pathname === "/document-types")}>
            <FileText size={17} className="shrink-0" />{!collapsed && <span>Document Types</span>}
          </button>
          <button onClick={() => nav("/profile")} data-testid="nav-profile" className={navCls(loc.pathname === "/profile")}>
            <UserRound size={17} className="shrink-0" />{!collapsed && <span>My Profile</span>}
          </button>
          <button onClick={() => { setShowSupport(true); setMobileOpen(false); }} data-testid="nav-support" className={navCls(false)}>
            <LifeBuoy size={17} className="shrink-0" />{!collapsed && <span>Support</span>}
          </button>
        </div>

        <div className="border-t border-white/8 p-3">
          <div className="flex items-center gap-3">
            <button onClick={() => nav("/profile")} data-testid="nav-profile-name" className="flex items-center gap-3 flex-1 min-w-0 text-left hover:opacity-80 transition-opacity">
              <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center text-[11px] font-semibold text-secondary-foreground shrink-0">
                {initials(user?.name)}
              </div>
              {!collapsed && (
                <div className="flex-1 overflow-hidden">
                  <div className="text-[13px] text-foreground truncate">{user?.name}</div>
                  <div className="text-[10px] text-muted-foreground uppercase tracking-wide truncate">{user?.tenant_name || user?.role}</div>
                </div>
              )}
            </button>
            {!collapsed && (
              <button onClick={logout} data-testid="logout-btn" className="text-muted-foreground hover:text-foreground">
                <LogOut size={16} />
              </button>
            )}
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 shrink-0 surface border-b border-white/8 flex items-center px-4 md:px-5 gap-3">
          <button onClick={() => setMobileOpen(true)} data-testid="firm-mobile-menu" className="md:hidden flex items-center gap-2 min-w-0 lift">
            {user?.logo_url
              ? <img src={user.logo_url} alt={firmName} className="w-8 h-8 rounded-lg object-contain shrink-0 bg-white/5" />
              : <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${brandAccent}22`, border: `1px solid ${brandAccent}55` }}><Building2 size={16} style={{ color: brandAccent }} /></div>}
            <span className="font-display font-semibold text-[14px] truncate max-w-[150px]">{firmName}</span>
          </button>
          <div className="hidden md:flex min-w-0"><Breadcrumbs matter={matter} loc={loc} /></div>
          <div className={`ml-auto items-center gap-2 ${loc.pathname === "/phone-dialer" ? "hidden md:flex" : "flex"}`}>
          <AppNotificationsBell />
          <button
            onClick={() => nav(matterId ? `/focused?matter=${matterId}` : "/focused")}
            data-testid="switch-focused"
            className="flex items-center gap-1.5 surface-2 hairline px-2.5 py-1.5 rounded-lg text-[12px] text-muted-foreground hover:text-foreground lift"
          >
            <Crosshair size={13} /> Focused view
          </button>
          <button onClick={toggle} data-testid="theme-toggle"
            className="flex items-center justify-center w-8 h-8 surface-2 hairline rounded-lg text-muted-foreground hover:text-foreground lift">
            {theme === "dark" ? <Sun size={15} /> : <Moon size={15} />}
          </button>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto">
          <PhonePendingBanner />
          {user?.slug === "demo-firm" && <div className={loc.pathname === "/phone-dialer" ? "hidden sm:block" : ""}><ContactSuggestionBanner /></div>}
          {showSupport ? (
            <div className="h-full"><SupportChat themed apiClient={api} base="/support" onClose={() => setShowSupport(false)} /></div>
          ) : <Outlet />}
          <IdleLogout
            minutes={user?.platform_role === "owner" ? 15 : user?.tenant_role === "admin" ? 15 : 30}
            onLogout={logout}
            accent={brandAccent}
          />
        </main>
      </div>
    </div>
  );
}

function PhonePendingBanner() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const load = () => api.get("/firm/phone/pending-choices").then((r) => setRows(r.data || [])).catch(() => setRows([]));
  useEffect(() => {
    load();
    const t = setInterval(() => { if (!document.hidden) load(); }, 60000);
    window.addEventListener("focus", load);
    window.addEventListener("phone-calls-changed", load);
    return () => { clearInterval(t); window.removeEventListener("focus", load); window.removeEventListener("phone-calls-changed", load); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  if (!rows.length) return null;
  const dismiss = async () => { try { await Promise.all(rows.map((c) => api.post(`/firm/phone/calls/${c.id}/dismiss`))); } catch {} load(); };
  return (
    <div className="mx-4 sm:mx-6 lg:mx-8 mt-4 rounded-xl border border-amber-500/30 bg-amber-500/[0.08] px-4 py-3 flex items-center gap-3 flex-wrap" data-testid="phone-pending-banner">
      <Phone size={16} className="text-amber-500 shrink-0" />
      <span className="text-[13px] text-foreground/85 flex-1 min-w-0">{rows.length} phone call{rows.length === 1 ? " matches" : "s match"} more than one matter. Choose where {rows.length === 1 ? "it goes" : "they go"}.</span>
      <button onClick={() => nav("/phone-calls")} data-testid="phone-banner-review" className="text-[12.5px] font-semibold px-3 py-1.5 rounded-lg bg-primary text-primary-foreground lift">Review</button>
      <button onClick={dismiss} data-testid="phone-banner-dismiss" className="text-[12.5px] px-3 py-1.5 rounded-lg surface-2 hairline lift">Dismiss</button>
    </div>
  );
}

function Breadcrumbs({ matter, loc }) {
  if (!matter) return null;
  const parts = loc.pathname.split("/").filter(Boolean);
  const labelMap = { package: "Package & Services", claims: "Claims", research: "Legal Research", documents: "Documents", process: "Client Progress", evidence: "Evidence Review", activity: "Activity", assistant: "AI Assistant" };
  return (
    <div className="flex items-center gap-2 text-sm min-w-0">
      <Link to="/" className="text-muted-foreground hover:text-foreground">Matters</Link>
      {matter && (
        <>
          <span className="text-white/20">/</span>
          <Link to={`/matters/${matter.id}`} className="text-foreground truncate max-w-[280px]">{matter.name}</Link>
          {parts[2] && labelMap[parts[2]] && (
            <>
              <span className="text-white/20">/</span>
              <span className="text-muted-foreground">{labelMap[parts[2]]}</span>
            </>
          )}
        </>
      )}
    </div>
  );
}
